import { AnimatePresence, motion } from "framer-motion";
import { useEffect } from "react";
import { classNames } from "primereact/utils";
import styles from "../WorkoutPlanDialog.module.scss";

/**
 * Common rest-timer presets (in seconds). 0 means "OFF". The list mirrors
 * what mainstream lifting apps offer so the option the user wants is almost
 * always one tap away.
 */
const PRESETS: number[] = [
  0, 30, 45, 60, 75, 90, 105, 120, 150, 180, 210, 240, 300,
];

const formatLabel = (seconds: number): string => {
  if (seconds <= 0) return "OFF";
  if (seconds < 60) return `${seconds}с`;
  const minutes = Math.floor(seconds / 60);
  const remainder = seconds % 60;
  if (remainder === 0) return `${minutes}мин`;
  return `${minutes}:${remainder.toString().padStart(2, "0")}`;
};

interface IRestTimerPickerProps {
  isOpen: boolean;
  currentSeconds: number;
  useAutomaticTimer: boolean;
  onClose: () => void;
  onPick: (seconds: number) => void;
  onToggleAutomaticTimer: (useAutomatic: boolean) => void;
}

/**
 * Bottom-sheet-style picker for the per-exercise rest timer. Includes the
 * "use platform's automatic timer" toggle that controls whether checking a set
 * actually starts the countdown — having the choice next to the duration keeps
 * the related decisions together.
 */
export const RestTimerPicker = ({
  isOpen,
  currentSeconds,
  useAutomaticTimer,
  onClose,
  onPick,
  onToggleAutomaticTimer,
}: IRestTimerPickerProps) => {
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, onClose]);

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className={styles.rest_picker_backdrop}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.18, ease: "easeOut" }}
          onClick={(e) => {
            // Only close when the user actually clicks the backdrop, not when a
            // click propagates up from a preset button.
            if (e.target === e.currentTarget) onClose();
          }}
        >
          <motion.div
            className={styles.rest_picker_sheet}
            initial={{ y: 24, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 24, opacity: 0 }}
            transition={{ duration: 0.22, ease: "easeOut" }}
            role="dialog"
            aria-label="Избери време за почивка"
          >
            <div className={styles.rest_picker_title}>Почивка</div>
            <div className={styles.rest_picker_sub}>
              Избери колко секунди да тече таймерът след всеки сет.
            </div>
            <div className={styles.rest_picker_grid}>
              {PRESETS.map((seconds) => (
                <button
                  key={seconds}
                  type="button"
                  className={classNames(styles.rest_picker_chip, {
                    [styles.rest_picker_chip_active]:
                      seconds === currentSeconds,
                    [styles.rest_picker_chip_off]: seconds === 0,
                  })}
                  onClick={() => {
                    onPick(seconds);
                    onClose();
                  }}
                >
                  {formatLabel(seconds)}
                </button>
              ))}
            </div>

            <label className={styles.rest_picker_toggle}>
              <input
                type="checkbox"
                checked={useAutomaticTimer}
                onChange={(event) =>
                  onToggleAutomaticTimer(event.target.checked)
                }
              />
              <span className={styles.rest_picker_toggle_box} aria-hidden>
                {useAutomaticTimer ? "✓" : ""}
              </span>
              <span className={styles.rest_picker_toggle_text}>
                <span className={styles.rest_picker_toggle_label}>
                  Използвай автоматичния таймер на платформата
                </span>
                <span className={styles.rest_picker_toggle_sub}>
                  Когато е включено, отметката на сет стартира автоматичен
                  таймер.
                </span>
              </span>
            </label>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export const formatRestTimerLabel = formatLabel;
